/**
 * Leads — what the newsletters found that the venue adapters do not cover,
 * and Doug's decision on each: promote it, or say it is not for him.
 *
 * Moved here from the phone app's Saved screen, 22 September, on Doug's own
 * steer: newsletters should mostly populate events against venues the
 * catalogue already has, not grow the catalogue on a lead's `place_guess`
 * alone — "this should be where management happens." The phone screen still
 * dismisses a lead on the move; promoting one at a venue not already on the
 * list, which is the one action with a real decision behind it, lives here.
 *
 * Venue matching is two gates, cheapest first — see `app/src/lib/leadVenue.ts`
 * and the Worker's `src/leadVenue.ts` for why each exists:
 *
 *   1. `localVenueMatch` — free, client-side, exact containment against a
 *      name or alias already in `venues`. Most leads clear this; the fold
 *      already handles spelling differences like "Deutsches Theatre".
 *   2. `checkLeadVenue` — a tap, not automatic. Widens the dedupe (catches a
 *      near-miss the exact fold above does not) and, only if that finds
 *      nothing either, spends one web-search-backed model call asking
 *      whether the place is real and current. No independent source, no
 *      venue — the same rule `exhibition-prose.ts` already proved out.
 *
 * A verified place is shown as a draft, unwritten, until Doug taps "Add
 * venue" — this route deliberately does not insert on a `check` result by
 * itself.
 *
 * A third path sits beside those two: "Add a new venue" opens a plain form.
 * Zenner — a real Biergarten at Treptower Park — turned up in a lead with
 * neither a local match nor anything the web check could source, which is
 * exactly the gap `20260922101000_bushy_pony.sql` already named: the
 * newest and smallest places are sometimes in no directory and no search
 * result either, and Doug's own knowledge that a place is real is the only
 * source there is. That draft carries `source_url: null` rather than one
 * made up to satisfy the check, and the admin route logs which kind of add
 * it was rather than pretending every venue arrived the same way.
 *
 * 23 September, after Doug reported trying to add Zenner and it "not
 * working": the actual write, checked directly against Postgres, succeeds
 * for exactly this draft shape — no bug found in `leadVenue()`'s `add`
 * action itself. What was missing is what made a real failure look like a
 * dead end: `check`'s own result left no way back once it stopped being
 * `idle` (an error or a `not_found` had no retry, only a page reload), and
 * a `not_found` did not point at the one path built for it — "Add a new
 * venue" sat as a quiet button below, easy to miss right when it is the
 * answer. Both fixed: an error or `not_found` now offers to try again, and
 * `not_found` opens the manual form itself rather than waiting to be
 * found. Neither is a confirmed root cause, since this session has no
 * Supabase session of Doug's own to reproduce the report against — if it
 * still fails, what the retry shows is the next real lead.
 *
 * The same session added a free, local hint for the other half of Doug's
 * question — why a lead for a venue already on the list still sits here at
 * all: most of the time the venue was never the gap, the *event* just has
 * not been told apart from one the venue's own recipe already harvested.
 * `possibleDuplicate` in `leadVenue.ts` checks the lead's title and dates
 * against that venue's own upcoming rows and prints "likely already on the
 * list" when they look like the same night — a hint, never a decision made
 * for him, and it says nothing when the title is in a different language
 * than the harvested one (a German newsletter against an English-page
 * recipe shares too few words to say anything with confidence).
 */
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../app/src/lib/supabase'
import { dismissLead, promoteLead } from '../../app/src/lib/db'
import { PROMOTE_TYPES } from '../../app/src/lib/leadPromote'
import {
  localVenueMatch, checkLeadVenue, addLeadVenue, possibleDuplicate,
  type VenueDraft, type VenueEventLite,
} from '../../app/src/lib/leadVenue'
import { fetchUpcomingEvents } from '../../app/src/lib/venueRecord'
import { KINDS } from './venueGroups'
import type { Lead } from '../../app/src/lib/types'

type LeadVenue = { id: string; name: string; aliases: string[] | null }
type StatusFilter = Lead['status'] | 'all'

const STATUSES: { id: StatusFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'promoted', label: 'Promoted' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'expired', label: 'Expired' },
  { id: 'all', label: 'All' },
]

function useLeads(status: StatusFilter) {
  const [leads, setLeads] = useState<Lead[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function refetch() {
    let q = supabase.from('leads').select('*').order('created_at', { ascending: false })
    if (status !== 'all') q = q.eq('status', status)
    const r = await q
    if (r.error) setError(r.error.message)
    else { setLeads((r.data ?? []) as Lead[]); setError(null) }
  }

  useEffect(() => { setLeads(null); void refetch() }, [status]) // eslint-disable-line react-hooks/exhaustive-deps

  return { leads, error, refetch }
}

function useAllVenues() {
  const [venues, setVenues] = useState<LeadVenue[]>([])
  const [error, setError] = useState<string | null>(null)

  async function refetch() {
    const r = await supabase.from('venues').select('id,name,aliases').order('name')
    if (r.error) setError(r.error.message)
    else { setVenues((r.data ?? []) as LeadVenue[]); setError(null) }
  }

  useEffect(() => { void refetch() }, [])

  return { venues, error, addLocal: (v: LeadVenue) => setVenues(prev => [...prev, v].sort((a, b) => a.name.localeCompare(b.name))) }
}

const fmtDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

function whenText(dates: string[]): string {
  if (dates.length === 0) return 'No date given'
  if (dates.length === 1) return fmtDate(dates[0])
  return `${fmtDate(dates[0])} – ${fmtDate(dates[dates.length - 1])}`
}

export function LeadsPanel() {
  const [status, setStatus] = useState<StatusFilter>('open')
  const { leads, error, refetch } = useLeads(status)
  const { venues, error: venuesError, addLocal } = useAllVenues()

  return (
    <div className="leads">
      <div className="statsbar">
        <span><b>{leads?.length ?? 0}</b> {STATUSES.find(s => s.id === status)?.label.toLowerCase()}</span>
        <select value={status} onChange={e => setStatus(e.target.value as StatusFilter)}>
          {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {(error || venuesError) && <span className="err">{error ?? venuesError}</span>}
      </div>

      <div className="leads-body">
        {!leads && !error && <p className="dim">Reading leads…</p>}
        {leads && leads.length === 0 && <p className="dim">Nothing here.</p>}
        {leads?.map(l => (
          <LeadCard key={l.id} lead={l} venues={venues} onVenueAdded={addLocal}
                    onChanged={() => void refetch()} readOnly={status !== 'open'} />
        ))}
      </div>
    </div>
  )
}

type CheckState =
  | { status: 'idle' }
  | { status: 'busy' }
  | { status: 'duplicate'; venue: { id: string; name: string } }
  | { status: 'verified'; draft: VenueDraft }
  | { status: 'not_found'; reason: string }
  | { status: 'error'; message: string }

function draftAddress(d: VenueDraft): string | null {
  const cityLine = [d.postcode, d.city].filter(Boolean).join(' ')
  const parts = [d.street, cityLine || null, d.district ? `(${d.district})` : null].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

/**
 * A venue Doug is adding by name, not one a search verified.
 *
 * No `source_url` field to fill in — that would ask him to invent one, which
 * is exactly the failure `exhibition-prose.ts`'s guardrail exists to catch
 * on the other path. Knowing the place is real *is* the source here, the
 * same as it was for Bushy Pony.
 */
function ManualVenueForm({ initialName, onAdd, onCancel, busy }: {
  initialName: string
  onAdd: (draft: VenueDraft) => void
  onCancel: () => void
  busy: boolean
}) {
  const [name, setName] = useState(initialName)
  const [kind, setKind] = useState('other')
  const [street, setStreet] = useState('')
  const [postcode, setPostcode] = useState('')
  const [city, setCity] = useState('Berlin')
  const [district, setDistrict] = useState('')
  const [siteUrl, setSiteUrl] = useState('')

  const canAdd = !!name.trim() && !busy

  function submit() {
    if (!canAdd) return
    onAdd({
      name: name.trim(),
      kind,
      street: street.trim() || null,
      postcode: postcode.trim() || null,
      city: city.trim() || null,
      district: district.trim() || null,
      site_url: siteUrl.trim() || null,
      source_url: null,
    })
  }

  return (
    <div className="venueform">
      <label>Name
        <input value={name} onChange={e => setName(e.currentTarget.value)} />
      </label>
      <label>Kind
        <select value={kind} onChange={e => setKind(e.target.value)}>
          {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <label>Street
        <input value={street} onChange={e => setStreet(e.currentTarget.value)} />
      </label>
      <label>Postcode
        <input value={postcode} onChange={e => setPostcode(e.currentTarget.value)} />
      </label>
      <label>City
        <input value={city} onChange={e => setCity(e.currentTarget.value)} />
      </label>
      <label>District
        <input value={district} onChange={e => setDistrict(e.currentTarget.value)} />
      </label>
      <label className="venueform__wide">Website
        <input value={siteUrl} placeholder="https://…" onChange={e => setSiteUrl(e.currentTarget.value)} />
      </label>
      <div className="venueform__actions">
        <button className="btn btn-primary" disabled={!canAdd} onClick={submit}>
          {busy ? 'Adding…' : 'Add venue'}
        </button>
        <button className="btn btn-quiet" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function LeadCard({ lead, venues, onVenueAdded, onChanged, readOnly }: {
  lead: Lead
  venues: LeadVenue[]
  onVenueAdded: (v: LeadVenue) => void
  onChanged: () => void
  readOnly: boolean
}) {
  const x = lead.extracted
  const localMatch = useMemo(() => localVenueMatch(x?.place_guess ?? null, venues), [x?.place_guess, venues])

  const [venueId, setVenueId] = useState<string | null>(localMatch?.id ?? null)
  const [check, setCheck] = useState<CheckState>({ status: 'idle' })
  const [manualOpen, setManualOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [duplicate, setDuplicate] = useState<VenueEventLite | null>(null)

  // A different lead's card is a different place to start from — otherwise
  // the venue picked for the previous card would still be selected here.
  useEffect(() => {
    setVenueId(localMatch?.id ?? null)
    setCheck({ status: 'idle' })
    setManualOpen(false)
  }, [lead.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Answers "why is a lead for a venue I already have still sitting here" —
  // most of the time it is not the venue, it is that nothing has told the
  // app the newsletter's mention and the recipe's own harvest are the same
  // night. Only runs once the venue is already known locally: a lead with
  // no match has nothing to check duplication against yet, and this must
  // stay free — no model call, unlike "Check venue" below.
  useEffect(() => {
    setDuplicate(null)
    if (!localMatch) return
    let live = true
    fetchUpcomingEvents(localMatch.id, 100)
      .then(events => {
        if (live) setDuplicate(possibleDuplicate({ title: x?.title ?? null, dates: x?.dates ?? [] }, events))
      })
      .catch(() => {}) // a hint that failed to load is not worth an error banner
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, localMatch?.id])

  async function runCheck() {
    if (!x?.place_guess) return
    setCheck({ status: 'busy' })
    try {
      const result = await checkLeadVenue(x.place_guess)
      setCheck(result)
      if (result.status === 'duplicate') setVenueId(result.venue.id)
      // A search finding nothing does not mean the place is not real — see
      // the Zenner note above the venue-matching block. Opening the manual
      // form here rather than leaving it a quiet button below is what makes
      // that path something Doug actually reaches, not just something that
      // exists.
      if (result.status === 'not_found') setManualOpen(true)
    } catch (e) {
      setCheck({ status: 'error', message: e instanceof Error ? e.message : String(e) })
    }
  }

  async function confirmAdd(draft: VenueDraft) {
    setBusy(true)
    try {
      const venue = await addLeadVenue(draft)
      onVenueAdded({ id: venue.id, name: venue.name, aliases: [venue.name] })
      setVenueId(venue.id)
      setCheck({ status: 'duplicate', venue })
      setManualOpen(false)
    } catch (e) {
      setCheck({ status: 'error', message: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  async function act(write: () => PromiseLike<{ error: { message: string } | null }>) {
    setBusy(true)
    setActionError(null)
    const { error } = await write()
    setBusy(false)
    if (error) setActionError(error.message)
    else onChanged()
  }

  const selectedVenue = venues.find(v => v.id === venueId) ?? null

  return (
    <div className="leadcard">
      <div className="leadcard__head">
        <b>{x?.title ?? 'Untitled'}</b>
        <span className="dim">{whenText(x?.dates ?? [])}</span>
      </div>

      <div className="leadcard__where">
        <span>{x?.place_guess ?? <span className="dim">no place stated</span>}</span>
        {x?.organiser_guess && <span className="dim"> · organiser: {x.organiser_guess}</span>}
        {lead.mail_source && <span className="dim"> · via {lead.mail_source.split('@')[1] ?? lead.mail_source}</span>}
      </div>

      {!readOnly && (
        <div className="leadcard__venue">
          {localMatch && <span className="tag tag-ok">known as {localMatch.name}</span>}
          {localMatch && duplicate && (
            <span className="dim">
              — likely already on the list, as{' '}
              <b>{duplicate.title}</b>
              {duplicate.run_start && <>, {whenText(duplicate.run_end ? [duplicate.run_start, duplicate.run_end] : [duplicate.run_start])}</>}
            </span>
          )}

          {!localMatch && (
            <>
              <select value={venueId ?? ''} onChange={e => setVenueId(e.target.value || null)}>
                <option value="">— file with no venue —</option>
                {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>

              {/* Nothing to check against a search when the newsletter named
                  no place at all — the manual form below is the only path
                  that still makes sense. */}
              {x?.place_guess && check.status === 'idle' && (
                <button className="btn btn-quiet" onClick={() => void runCheck()}>Check venue</button>
              )}
              {check.status === 'busy' && <span className="dim">Checking — a web search, a few cents…</span>}
              {/* `manualOpen` means this error came from `confirmAdd`, not
                  `runCheck` — the form is still on screen with what Doug
                  typed, and its own "Add venue" button is the retry. A
                  "Try again" here would re-run the *other* action. */}
              {check.status === 'error' && !manualOpen && (
                <span className="err">
                  {check.message}{' '}
                  <button className="btn btn-quiet" style={{ padding: '0 4px' }} onClick={() => void runCheck()}>
                    Try again
                  </button>
                </span>
              )}
              {check.status === 'error' && manualOpen && <span className="err">{check.message}</span>}

              {check.status === 'duplicate' && (
                <span className="tag tag-ok">on the list, as {check.venue.name}</span>
              )}

              {check.status === 'not_found' && (
                <span className="dim">
                  No independent source found ({check.reason}) — file with no venue, pick one above, or{' '}
                  <button className="btn btn-quiet" style={{ padding: '0 4px' }} onClick={() => void runCheck()}>
                    check again
                  </button>.
                </span>
              )}

              {check.status === 'verified' && (
                <div className="venuedraft">
                  <p>
                    <b>{check.draft.name}</b>{check.draft.kind ? ` · ${check.draft.kind}` : ''}
                    {draftAddress(check.draft) && <span className="dim"> · {draftAddress(check.draft)}</span>}
                  </p>
                  <p className="dim">
                    Verified via{' '}
                    <a href={check.draft.source_url!} target="_blank" rel="noopener noreferrer">source</a>
                    {check.draft.site_url && <> · <a href={check.draft.site_url} target="_blank" rel="noopener noreferrer">site</a></>}
                  </p>
                  <button className="btn btn-primary" disabled={busy} onClick={() => void confirmAdd(check.draft)}>
                    {busy ? 'Adding…' : 'Add venue'}
                  </button>
                </div>
              )}

              {!manualOpen && (
                <button className="btn btn-quiet" onClick={() => setManualOpen(true)}>
                  Add a new venue
                </button>
              )}
              {manualOpen && (
                <ManualVenueForm
                  initialName={x?.place_guess ?? ''}
                  busy={busy}
                  onCancel={() => setManualOpen(false)}
                  onAdd={draft => void confirmAdd(draft)}
                />
              )}
            </>
          )}
        </div>
      )}

      <details className="leadcard__text">
        <summary>What the newsletter said</summary>
        <p>{lead.text}</p>
      </details>

      {!readOnly && (
        <div className="leadcard__actions">
          {PROMOTE_TYPES.map(t => (
            <button key={t.type} className="btn btn-primary" disabled={busy}
                    onClick={() => void act(() => promoteLead(lead, t.type, selectedVenue?.id ?? venueId ?? null))}>
              {t.label}
            </button>
          ))}
          <button className="btn btn-quiet" disabled={busy} onClick={() => void act(() => dismissLead(lead.id))}>
            Not for me
          </button>
          {x?.url && (
            <a className="btn btn-quiet" href={x.url} target="_blank" rel="noopener noreferrer">Site</a>
          )}
        </div>
      )}
      {actionError && <p className="err">{actionError}</p>}
    </div>
  )
}
